import { Chart as ChartJS, CategoryScale, LinearScale, RadialLinearScale, PointElement, LineElement, BarElement, ArcElement, Filler, Tooltip, Legend, LineController, BarController, PieController, DoughnutController, RadarController } from 'chart.js';
import { Pie, Doughnut, Radar, Chart } from 'react-chartjs-2';
import DataLabels from 'chartjs-plugin-datalabels';
import type { ReactNode } from 'react';
import type { ChartConfig, ChartBackendData } from '@/types/chart';
import { transformToChartJs, mapChartType } from './chartUtils';
import './ChartWidget.css';
import '@/utils/registerFunnel';
import { RadialGaugeChart } from './RadialGaugeChart';

// Register all Chart.js components ONCE
ChartJS.register(
  LineController, BarController, PieController, DoughnutController, RadarController,
  CategoryScale, LinearScale, RadialLinearScale,
  PointElement, LineElement, BarElement, ArcElement,
  Filler, Tooltip, Legend,
  DataLabels,
);

interface ChartWidgetProps {
  config: ChartConfig;
  data?: ChartBackendData;
}

export function ChartWidget({ config, data }: ChartWidgetProps) {
  let content: ReactNode;

  // Guard 1: backend returned error field
  if (data && typeof data === 'object' && 'error' in data && typeof data.error === 'string') {
    content = <div className="chart-widget-error text-danger">{data.error}</div>;
  }
  // Guard 2: data is null/undefined/non-object/array
  else if (!data || typeof data !== 'object' || Array.isArray(data)) {
    content = <div className="chart-widget-empty text-muted">No data available</div>;
  }
  // Guard 3: series missing or not an array
  else if (!Array.isArray(data.series)) {
    content = (
      <div className="chart-widget-error text-danger">
        Invalid chart data: missing or invalid series
      </div>
    );
  }
  // Guard 4: series is empty
  else if (data.series.length === 0) {
    content = <div className="chart-widget-empty text-muted">No data available</div>;
  }
  // Guard 5: wrap transformToChartJs in try-catch to prevent Chart.js crashes
  else {
    let chartJsData!: ReturnType<typeof transformToChartJs>['chartJsData'];
    let chartJsOptions!: ReturnType<typeof transformToChartJs>['chartJsOptions'];
    try {
      const result = transformToChartJs(data, config);
      chartJsData = result.chartJsData;
      chartJsOptions = result.chartJsOptions;
    } catch (err) {
      content = (
        <div className="chart-widget-error text-danger">
          Failed to render chart: {err instanceof Error ? err.message : String(err)}
        </div>
      );
    }

    // If transform succeeded, determine chart content
    if (!content) {
      const chartType = config.chartType;
      const chartJsType = mapChartType(chartType);

      // Determine effective render type from per-series overrides.
      // If ALL series override to the SAME non-cartesian type, use it for rendering.
      // This handles cases like global=pie but all override=funnel.
      // Cartesian overrides (bar, line, area, hbar) are handled by Chart.js
      // natively via per-dataset type and don't need a component switch.
      const overrideTypes = [...new Set(
        config.series.map((s) => s.chartTypeOverride).filter(Boolean)
      )];
      const candidateJsType = overrideTypes.length === 1
        ? mapChartType(overrideTypes[0]!)
        : null;
      const isNonCartesian = candidateJsType && !['line', 'bar'].includes(candidateJsType);
      const renderJsType = isNonCartesian ? candidateJsType : chartJsType;

      const orientation = config.funnelOrientation ?? config.series[0]?.funnelOrientation ?? 'vertical';
      const renderType = chartType === 'funnel' ? orientation : 'default';
      const seriesKey = config.series.map((s) => `${s.chartTypeOverride || 'default'}-${renderType}`).join(',');

      // Non-cartesian types: render as dedicated components
      if (renderJsType === 'pie') {
        content = <Pie key={`pie-${seriesKey}`} data={chartJsData} options={chartJsOptions} />;
      } else if (renderJsType === 'doughnut') {
        // Check if all overrides point to a specific original type (radialGauge maps to doughnut)
        const effectiveChartType = overrideTypes.length === 1 ? overrideTypes[0] : chartType;
        if (effectiveChartType === 'radialGauge') {
          content = <RadialGaugeChart key={`radialGauge-${seriesKey}`} data={chartJsData} options={chartJsOptions} />;
        } else {
          content = <Doughnut key={`doughnut-${seriesKey}`} data={chartJsData} options={chartJsOptions} />;
        }
      } else if (renderJsType === 'funnel') {
        const chartProps = { data: chartJsData, options: chartJsOptions };
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        content = <Chart key={`funnel-${seriesKey}`} type="funnel" {...chartProps as any} plugins={[DataLabels]} />;
      } else if (renderJsType === 'radar') {
        content = <Radar key={`radar-${seriesKey}`} data={chartJsData} options={chartJsOptions} />;
      } else if (renderJsType === 'line' || renderJsType === 'bar') {
        const hasHBarOverride = config.series.some((s) => s.chartTypeOverride === 'hbar');
        const effectiveRenderType = hasHBarOverride ? 'bar' : renderJsType;
        const finalOptions = {
          ...chartJsOptions,
          ...(hasHBarOverride ? { indexAxis: 'y' as const } : {}),
        };

        content = <Chart type={effectiveRenderType} data={chartJsData} options={finalOptions} key={`${effectiveRenderType}-${seriesKey}`} />;
      } else {
        content = <div className="chart-widget-error text-danger">Unknown chart type: {chartType}</div>;
      }
    }
  }

  return (
    <div className="d-flex flex-column h-100" style={{ minHeight: 0 }}>
      <div style={{ flex: '1 1 auto', minHeight: 0, position: 'relative' }}>
        {content}
      </div>
      {config.description && (
        <div
          className="flex-shrink-0 mt-2 pb-1 small text-secondary w-100"
          style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
        >
          {config.description}
        </div>
      )}
    </div>
  );
}
